// =============================================================================
// ParityReportTable — wynik analizy: bramki (G1..G6), tabela stron, brakujace
// adresy WP z propozycja redirectu, lista redirectow zapisanych w DB.
// =============================================================================

import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, XCircle, AlertTriangle, Minus, Plus, Trash2, Eye, ExternalLink } from "lucide-react";
import type { ParityReport, PageParity, StaticRedirectRow, RenderedFile, SiteModel } from "@/services/staticSite/types";
import { filePathForUrlPath } from "@/services/staticSite/urlPolicy";
import { previewHtml } from "@/services/staticSite/migrationRunner";

const KIND_LABEL: Record<string, string> = {
  home: "Home", article: "Artykuł", pillar: "Pillar", author: "Autor", subpage: "Podstrona",
  category: "Kategoria", legacy_post: "Stary wpis WP", legacy_page: "Stara strona WP", not_found: "404",
};

function GateChip({ value, hard }: { value: boolean | null; hard?: boolean }) {
  if (value === null) return <Minus className="h-3.5 w-3.5 text-muted-foreground/50" />;
  if (value) return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
  return hard ? <XCircle className="h-4 w-4 text-red-500" /> : <AlertTriangle className="h-4 w-4 text-amber-500" />;
}

interface Props {
  report: ParityReport;
  site: SiteModel | null;
  files: RenderedFile[];
  redirects: StaticRedirectRow[];
  onAddRedirect: (row: Omit<StaticRedirectRow, "id" | "site_id">) => Promise<void>;
  onRemoveRedirect: (id: string) => Promise<void>;
}

const PAGE_SIZE = 25;
const LIST_STEP = 50;

export function ParityReportTable({ report, site, files, redirects, onAddRedirect, onRemoveRedirect }: Props) {
  const issuesCount = useMemo(() => report.pages.filter((p) => p.issues.length).length, [report.pages]);
  const [filter, setFilter] = useState("");
  // Domyslnie tylko strony z uwagami (215 zielonych wierszy to szum); gdy 0 uwag — wszystkie
  const [onlyIssues, setOnlyIssues] = useState(issuesCount > 0);
  const [page, setPage] = useState(0);
  const [missingShown, setMissingShown] = useState(LIST_STEP);
  const [redirectsShown, setRedirectsShown] = useState(LIST_STEP);
  const [preview, setPreview] = useState<{ page: PageParity; html: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [manualFrom, setManualFrom] = useState("");
  const [manualTo, setManualTo] = useState("");

  const rows = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return report.pages.filter((p) => {
      if (onlyIssues && !p.issues.length) return false;
      if (!f) return true;
      return p.path.toLowerCase().includes(f) || p.title.toLowerCase().includes(f) || (p.sourceUrl || "").toLowerCase().includes(f);
    });
  }, [report.pages, filter, onlyIssues]);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const openPreview = (p: PageParity) => {
    if (!site) return;
    const file = files.find((f) => f.path === filePathForUrlPath(p.path));
    if (!file) return;
    setPreview({ page: p, html: previewHtml(file, site.baseUrl, files) });
  };

  const addGap = async (from: string, to: string | null) => {
    if (!to) return;
    setBusy(from);
    try {
      await onAddRedirect({ from_path: from, to_path: to, code: 301, source: "sitemap_gap", reason: "Z analizy sitemap WP" });
    } finally {
      setBusy(null);
    }
  };

  const addManual = async () => {
    const from = manualFrom.trim();
    const to = manualTo.trim();
    if (!from.startsWith("/") || !to.startsWith("/")) return;
    setBusy("manual");
    try {
      await onAddRedirect({ from_path: from, to_path: to, code: 301, source: "manual", reason: null });
      setManualFrom(""); setManualTo("");
    } finally {
      setBusy(null);
    }
  };

  const dbRedirectFroms = new Set(redirects.map((r) => r.from_path));

  return (
    <div className="space-y-5">
      {/* Bramki */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {report.gates.map((g) => (
          <div key={g.id} className={`rounded-md border p-3 text-sm ${g.passed ? "border-emerald-500/30 bg-emerald-500/5" : g.level === "hard" ? "border-red-500/40 bg-red-500/5" : "border-amber-500/40 bg-amber-500/5"}`}>
            <div className="flex items-center gap-2">
              <GateChip value={g.passed} hard={g.level === "hard"} />
              <span className="font-medium">{g.id} · {g.label}</span>
              <Badge variant="outline" className="ml-auto text-[10px]">{g.level === "hard" ? "blokuje" : "ostrzeżenie"}</Badge>
            </div>
            {!g.passed && g.issues.length > 0 && (
              <ul className="mt-2 max-h-28 space-y-0.5 overflow-auto font-mono text-[11px] text-muted-foreground">
                {g.issues.slice(0, 8).map((i, idx) => <li key={idx}>• {i}</li>)}
                {g.issues.length > 8 && <li>… i {g.issues.length - 8} więcej</li>}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Brakujace URL-e */}
      {report.missingUrls.length > 0 && (
        <div className="space-y-2 rounded-md border border-red-500/30 p-3">
          <div className="flex items-center gap-2 text-sm font-medium">
            <XCircle className="h-4 w-4 text-red-500" /> Adresy z WordPressa bez odpowiednika ({report.missingUrls.length})
          </div>
          <p className="text-xs text-muted-foreground">
            Każdy adres musi mieć plik albo przekierowanie 301, inaczej po migracji zwróci 404. Kliknij „Dodaj 301", żeby przyjąć propozycję, albo wpisz własne przekierowanie niżej.
          </p>
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Adres WP</TableHead>
                  <TableHead>Powód</TableHead>
                  <TableHead>Propozycja</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {report.missingUrls.slice(0, missingShown).map((m) => (
                  <TableRow key={m.url}>
                    <TableCell className="font-mono text-xs">{m.path}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{m.reason}</TableCell>
                    <TableCell className="font-mono text-xs">{m.suggestedRedirect || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>
                      {dbRedirectFroms.has(m.path) ? (
                        <Badge variant="secondary" className="text-[10px]">dodano</Badge>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!m.suggestedRedirect || busy === m.path} onClick={() => void addGap(m.path, m.suggestedRedirect)}>
                          <Plus className="mr-1 h-3 w-3" /> Dodaj 301
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {report.missingUrls.length > missingShown && (
              <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs" onClick={() => setMissingShown((n) => n + LIST_STEP)}>pokaż więcej ({report.missingUrls.length - missingShown})</Button>
            )}
          </div>
        </div>
      )}

      {/* Redirecty w DB + reczne */}
      <div className="space-y-2 rounded-md border p-3">
        <div className="text-sm font-medium">Przekierowania zapisane dla tej strony ({redirects.length})</div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-40">
            <Input value={manualFrom} onChange={(e) => setManualFrom(e.target.value)} placeholder="/stary-adres/" className="font-mono text-xs" />
          </div>
          <span className="text-muted-foreground">→</span>
          <div className="flex-1 min-w-40">
            <Input value={manualTo} onChange={(e) => setManualTo(e.target.value)} placeholder="/nowy-adres/" className="font-mono text-xs" />
          </div>
          <Button size="sm" variant="outline" onClick={() => void addManual()} disabled={busy === "manual" || !manualFrom.startsWith("/") || !manualTo.startsWith("/")}>
            <Plus className="mr-1 h-3 w-3" /> Dodaj ręcznie
          </Button>
        </div>
        {redirects.length > 0 && (
          <div>
            <Table>
              <TableBody>
                {redirects.slice(0, redirectsShown).map((r) => (
                  <TableRow key={r.id || r.from_path}>
                    <TableCell className="font-mono text-xs">{r.from_path}</TableCell>
                    <TableCell className="font-mono text-xs">→ {r.to_path}</TableCell>
                    <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{r.code} · {r.source}</Badge></TableCell>
                    <TableCell className="w-10">
                      {r.id && (
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => void onRemoveRedirect(r.id!)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {redirects.length > redirectsShown && (
              <Button size="sm" variant="ghost" className="mt-1 h-7 text-xs" onClick={() => setRedirectsShown((n) => n + LIST_STEP)}>pokaż więcej ({redirects.length - redirectsShown})</Button>
            )}
          </div>
        )}
        <p className="text-xs text-muted-foreground">
          Reguły automatyczne (wp-admin, wp-login, paginacja, tagi, archiwa autora, www→domena) są dodawane zawsze i nie wymagają wpisu.
        </p>
      </div>

      {/* Tabela stron */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-sm font-medium">Strony ({report.pages.length})</div>
          <Input value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }} placeholder="Filtruj po adresie / tytule" className="h-8 max-w-xs text-xs" />
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <input type="checkbox" checked={onlyIssues} onChange={(e) => { setOnlyIssues(e.target.checked); setPage(0); }} /> tylko z uwagami ({issuesCount})
          </label>
          <span className="ml-auto text-xs text-muted-foreground">G1 money · G2 linki · G4 obrazy/H2 · G5 meta · G6 schema</span>
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Typ</TableHead>
                <TableHead>Adres</TableHead>
                <TableHead className="text-center">$</TableHead>
                <TableHead className="text-center">G1</TableHead>
                <TableHead className="text-center">G2</TableHead>
                <TableHead className="text-center">G4</TableHead>
                <TableHead className="text-center">G5</TableHead>
                <TableHead className="text-center">G6</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageRows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-xs text-muted-foreground">{onlyIssues ? "Brak stron z uwagami." : "Brak wyników."}</TableCell></TableRow>
              )}
              {pageRows.map((p) => (
                <TableRow key={p.path} className={p.issues.length ? "bg-amber-500/5" : undefined}>
                  <TableCell><Badge variant="outline" className="text-[10px]">{KIND_LABEL[p.kind] || p.kind}</Badge></TableCell>
                  <TableCell>
                    <div className="font-mono text-xs">{p.path}</div>
                    <div className="max-w-md truncate text-xs text-muted-foreground" title={p.title}>{p.title}</div>
                    {p.issues.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                        {p.issues.slice(0, 4).map((i, idx) => <li key={idx}>• {i}</li>)}
                      </ul>
                    )}
                  </TableCell>
                  <TableCell className="text-center text-xs">{p.moneyLinks || <span className="text-muted-foreground/50">0</span>}</TableCell>
                  <TableCell className="text-center"><GateChip value={p.g1} hard /></TableCell>
                  <TableCell className="text-center"><GateChip value={p.g2} hard /></TableCell>
                  <TableCell className="text-center"><GateChip value={p.g4} /></TableCell>
                  <TableCell className="text-center"><GateChip value={p.g5} hard /></TableCell>
                  <TableCell className="text-center"><GateChip value={p.g6} /></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" title="Podgląd renderu" onClick={() => openPreview(p)} disabled={!site}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      {p.sourceUrl && (
                        <a href={p.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent" title="Otwórz na WP">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {rows.length > PAGE_SIZE && (
          <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
            <span>{safePage * PAGE_SIZE + 1}–{Math.min(rows.length, (safePage + 1) * PAGE_SIZE)} z {rows.length}</span>
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹</Button>
            <Button size="sm" variant="outline" className="h-7 px-2" disabled={safePage >= pageCount - 1} onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}>›</Button>
          </div>
        )}
      </div>

      <Dialog open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-6xl">
          <DialogHeader>
            <DialogTitle className="font-mono text-sm">{preview?.page.path}</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">WordPress (live)</div>
                {preview.page.sourceUrl ? (
                  <iframe title="wp" src={preview.page.sourceUrl} className="h-[70vh] w-full rounded border bg-white" sandbox="allow-same-origin" referrerPolicy="no-referrer" />
                ) : (
                  <div className="flex h-[70vh] items-center justify-center rounded border text-xs text-muted-foreground">Brak adresu WP (strona nowa)</div>
                )}
              </div>
              <div>
                <div className="mb-1 text-xs font-medium text-muted-foreground">Strona statyczna (render)</div>
                {/* srcdoc dziedziczy origin panelu -> NIGDY allow-same-origin */}
                <iframe title="static" srcDoc={preview.html} className="h-[70vh] w-full rounded border bg-white" sandbox="allow-scripts" referrerPolicy="no-referrer" />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
